import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  type HTMLAttributes,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useDropzone, type DropzoneOptions, type DropzoneState } from 'react-dropzone';
import { Input } from '@/components/ui-kit/input';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

type FileUploaderContextType = {
  dropzoneState: DropzoneState;
  isLOF: boolean;
  removeFileFromSet: (index: number) => void;
  activeIndex: number;
  setActiveIndex: Dispatch<SetStateAction<number>>;
};

const FileUploaderContext = createContext<FileUploaderContextType | null>(null);

const useFileUpload = () => {
  const context = useContext(FileUploaderContext);
  if (!context) throw new Error('useFileUpload must be used within FileUploader');
  return context;
};

type FileUploaderProps = {
  value: File[] | null;
  onValueChange: (value: File[] | null) => void;
  dropzoneOptions: DropzoneOptions;
  reSelect?: boolean;
};

export const FileUploader = forwardRef<
  HTMLDivElement,
  FileUploaderProps & HTMLAttributes<HTMLDivElement>
>(({ className, value, onValueChange, dropzoneOptions, reSelect, children, ...props }, ref) => {
  const { toast } = useToast();
  const maxFiles = dropzoneOptions.maxFiles ?? 1;
  const reSelectAll = maxFiles === 1 || reSelect;

  const removeFileFromSet = useCallback(
    (index: number) => {
      if (!value) return;
      onValueChange(value.filter((_, itemIndex) => itemIndex !== index));
    },
    [onValueChange, value]
  );

  const dropzoneState = useDropzone({
    ...dropzoneOptions,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        toast.error({ errors: rejectedFiles[0].errors[0]?.message || 'Invalid file' });
      }

      const nextFiles = reSelectAll ? [] : [...(value ?? [])];
      acceptedFiles.forEach((file) => {
        if (nextFiles.length < maxFiles) nextFiles.push(file);
      });
      onValueChange(nextFiles);
    },
  });

  return (
    <FileUploaderContext.Provider
      value={{
        dropzoneState,
        isLOF: (value?.length ?? 0) >= maxFiles,
        removeFileFromSet,
        activeIndex: -1,
        setActiveIndex: () => undefined,
      }}
    >
      <div ref={ref} className={cn('grid w-full overflow-hidden focus:outline-none', className)} {...props}>
        {children}
      </div>
    </FileUploaderContext.Provider>
  );
});

FileUploader.displayName = 'FileUploader';

export const FileInput = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const { dropzoneState, isLOF } = useFileUpload();
    const rootProps = isLOF ? {} : dropzoneState.getRootProps();

    return (
      <div ref={ref} {...props} className={cn('relative w-full', className)}>
        <div {...rootProps}>{children}</div>
        <Input
          ref={dropzoneState.inputRef}
          disabled={isLOF}
          {...dropzoneState.getInputProps()}
          className="sr-only"
        />
      </div>
    );
  }
);

FileInput.displayName = 'FileInput';
